"use client";

import { useCallback, useEffect, useRef, useState } from "react";
import {
  FileText,
  Download,
  Eye,
  Upload as UploadIcon,
  Trash2,
  Loader2,
  Paperclip,
  Image as ImageIcon,
} from "lucide-react";
import { message, Select, Modal, Input } from "antd";
import {
  taskDispatchFileApi,
  DISPATCH_FILE_TYPE_LABELS,
  type DispatchFile,
  type DispatchFileType,
} from "../../../services/taskDispatchFileApi";
import { ConfirmDialog } from "../../ui/confirm-dialog";

interface DispatchFilesSectionProps {
  taskId: number | null;
}

const MAX_SIZE_MB = 20;
const ACCEPT = "application/pdf,image/jpeg,image/png,image/webp";

export function DispatchFilesSection({ taskId }: DispatchFilesSectionProps) {
  const [files, setFiles] = useState<DispatchFile[]>([]);
  const [loading, setLoading] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [uploadModalOpen, setUploadModalOpen] = useState(false);
  const [pendingFile, setPendingFile] = useState<File | null>(null);
  const [pendingType, setPendingType] = useState<DispatchFileType>("appt_confirmation");
  const [pendingNotes, setPendingNotes] = useState("");
  const [fileToDelete, setFileToDelete] = useState<DispatchFile | null>(null);
  const [deleting, setDeleting] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const refresh = useCallback(async () => {
    if (!taskId) return;
    setLoading(true);
    try {
      const list = await taskDispatchFileApi.list(taskId);
      setFiles(list);
    } catch (err) {
      console.error("[DispatchFiles] list failed", err);
      message.error("Failed to load dispatch files");
    } finally {
      setLoading(false);
    }
  }, [taskId]);

  useEffect(() => {
    refresh();
  }, [refresh]);

  const handleFilePick = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > MAX_SIZE_MB * 1024 * 1024) {
      message.error(`File too large. Maximum ${MAX_SIZE_MB}MB.`);
      if (fileInputRef.current) fileInputRef.current.value = "";
      return;
    }
    setPendingFile(file);
    setUploadModalOpen(true);
    if (fileInputRef.current) fileInputRef.current.value = "";
  };

  const confirmUpload = async () => {
    if (!taskId || !pendingFile) return;
    setUploading(true);
    try {
      await taskDispatchFileApi.upload(taskId, pendingFile, pendingType, pendingNotes || undefined);
      setUploadModalOpen(false);
      setPendingFile(null);
      setPendingNotes("");
      await refresh();
    } catch (err: unknown) {
      const msg = (err as { response?: { data?: { message?: string } } })?.response?.data?.message
        || (err instanceof Error ? err.message : "Upload failed");
      message.error(msg);
    } finally {
      setUploading(false);
    }
  };

  const handleView = async (file: DispatchFile) => {
    if (!taskId) return;
    try {
      await taskDispatchFileApi.view(taskId, file);
    } catch (err) {
      console.error(err);
      message.error("Couldn't open this file.");
    }
  };

  const handleDownload = async (file: DispatchFile) => {
    if (!taskId) return;
    try {
      await taskDispatchFileApi.download(taskId, file);
    } catch (err) {
      console.error(err);
      message.error("Couldn't download this file.");
    }
  };

  const handleDelete = (file: DispatchFile) => {
    if (!taskId) return;
    setFileToDelete(file);
  };

  const confirmDelete = async () => {
    if (!taskId || !fileToDelete) return;
    setDeleting(true);
    try {
      await taskDispatchFileApi.delete(taskId, fileToDelete.id);
      setFileToDelete(null);
      await refresh();
    } catch (err) {
      console.error(err);
      message.error("Couldn't delete this file.");
    } finally {
      setDeleting(false);
    }
  };

  const formatSize = (bytes: number | null) => {
    if (!bytes) return "";
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(0)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  };

  const formatTime = (iso: string) => {
    const d = new Date(iso);
    return d.toLocaleString();
  };

  const getIcon = (mime: string | null) => {
    if (mime?.startsWith("image/")) return <ImageIcon className="h-5 w-5 text-purple-600" />;
    return <FileText className="h-5 w-5 text-red-600" />;
  };

  if (!taskId) return null;

  return (
    <div className="col-span-2 bg-indigo-50/40 border border-indigo-100 rounded-xl p-4">
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-2">
          <Paperclip className="h-5 w-5 text-indigo-600" />
          <label className="text-xs font-medium text-gray-700 uppercase tracking-wide">
            Files for Driver
          </label>
          <span className="text-xs text-gray-400 font-normal normal-case">
            (visible to the assigned driver in the mobile app)
          </span>
        </div>
        <div>
          <input
            ref={fileInputRef}
            type="file"
            accept={ACCEPT}
            className="hidden"
            onChange={handleFilePick}
          />
          <button
            type="button"
            onClick={() => fileInputRef.current?.click()}
            className="px-3 py-1.5 text-sm font-medium rounded-lg bg-indigo-600 text-white hover:bg-indigo-700 inline-flex items-center gap-1.5"
          >
            <UploadIcon className="h-4 w-4" />
            Upload
          </button>
        </div>
      </div>

      {loading ? (
        <div className="flex items-center justify-center py-6">
          <Loader2 className="h-5 w-5 animate-spin text-gray-400" />
          <span className="ml-2 text-sm text-gray-500">Loading…</span>
        </div>
      ) : files.length === 0 ? (
        <div className="text-center py-6 text-sm text-gray-500">
          No files uploaded yet. Click <b>Upload</b> to share appointment confirmations, gate passes, or other docs with the driver.
        </div>
      ) : (
        <div className="space-y-2">
          {files.map((file) => (
            <div
              key={file.id}
              className="flex items-center justify-between p-3 bg-white border border-gray-200 rounded-lg hover:border-indigo-300 transition-colors"
            >
              <div className="flex items-center gap-3 min-w-0">
                <div className="w-10 h-10 bg-gray-50 rounded-lg flex items-center justify-center flex-shrink-0">
                  {getIcon(file.mime_type)}
                </div>
                <div className="min-w-0">
                  <div className="flex items-center gap-2">
                    <span className="px-2 py-0.5 bg-indigo-100 text-indigo-700 rounded text-xs font-medium">
                      {DISPATCH_FILE_TYPE_LABELS[file.file_type]}
                    </span>
                    <p className="font-medium text-gray-900 truncate" title={file.file_name}>
                      {file.file_name}
                    </p>
                  </div>
                  <p className="text-xs text-gray-500 mt-0.5">
                    {formatSize(file.file_size)} · Uploaded {formatTime(file.uploaded_at)}
                    {file.uploader_name ? ` · by ${file.uploader_name}` : ""}
                  </p>
                  {file.notes && (
                    <p className="text-xs text-gray-600 italic mt-1">Note: {file.notes}</p>
                  )}
                </div>
              </div>
              <div className="flex gap-1.5 flex-shrink-0">
                <button
                  onClick={() => handleView(file)}
                  className="p-2 rounded-lg bg-gray-100 hover:bg-gray-200 text-gray-700"
                  title="View"
                >
                  <Eye className="h-4 w-4" />
                </button>
                <button
                  onClick={() => handleDownload(file)}
                  className="p-2 rounded-lg bg-gray-100 hover:bg-gray-200 text-gray-700"
                  title="Download"
                >
                  <Download className="h-4 w-4" />
                </button>
                <button
                  onClick={() => handleDelete(file)}
                  className="p-2 rounded-lg bg-red-50 hover:bg-red-100 text-red-600"
                  title="Delete"
                >
                  <Trash2 className="h-4 w-4" />
                </button>
              </div>
            </div>
          ))}
        </div>
      )}

      <Modal
        title="Upload Dispatch File"
        open={uploadModalOpen}
        onOk={confirmUpload}
        onCancel={() => {
          setUploadModalOpen(false);
          setPendingFile(null);
          setPendingNotes("");
        }}
        confirmLoading={uploading}
        okText="Upload"
      >
        <div className="space-y-3 pt-2">
          {pendingFile && (
            <div className="p-3 bg-gray-50 rounded-lg text-sm">
              <div className="font-medium text-gray-900">{pendingFile.name}</div>
              <div className="text-xs text-gray-500">
                {formatSize(pendingFile.size)} · {pendingFile.type}
              </div>
            </div>
          )}
          <div>
            <label className="block text-xs font-medium text-gray-700 mb-1">File Type</label>
            <Select
              value={pendingType}
              onChange={(v) => setPendingType(v as DispatchFileType)}
              options={Object.entries(DISPATCH_FILE_TYPE_LABELS).map(([value, label]) => ({
                value,
                label,
              }))}
              className="w-full"
            />
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-700 mb-1">Notes (optional)</label>
            <Input.TextArea
              value={pendingNotes}
              onChange={(e) => setPendingNotes(e.target.value)}
              rows={2}
              placeholder="Any extra context for the driver…"
              maxLength={500}
            />
          </div>
        </div>
      </Modal>

      <ConfirmDialog
        open={!!fileToDelete}
        onOpenChange={(v) => { if (!v) setFileToDelete(null); }}
        title="Delete file?"
        description={fileToDelete ? `"${fileToDelete.file_name}" will be permanently removed.` : ""}
        confirmLabel="Delete"
        variant="danger"
        loading={deleting}
        onConfirm={confirmDelete}
      />
    </div>
  );
}

export default DispatchFilesSection;
